import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import Button from '@/components/button/Button';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import { IconSuccessful16 } from '@cineamo/ui/src/icons/ic-successful';

import { track } from '@/utils/server-side-tracking';

import FormInput from './FormInput';
import FormTextarea from './FormTextarea';
import { sendEmailSchoolScreening } from './send-email';
import TurnstileWidget from './TurnstileWidget';

export type SchoolScreeningFormData = {
    preferredDate: string;
    preferredTime: string;
    movieWish: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    schoolName: string;
    studentCount: number;
    adultsCount: number;
    childrenAge: string;
    concessionSales: string;
    specialRequests: string;
};

type SchoolScreeningFormProps = {
    domainConfiguration: DomainConfigurationDto;
};

const CONCESSION_SALES_OPTIONS = ['yes', 'no'] as const;

export default function SchoolScreeningForm({ domainConfiguration }: SchoolScreeningFormProps) {
    const t = useTranslations();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

    const { register, handleSubmit, watch, reset } = useForm<SchoolScreeningFormData>({
        defaultValues: {
            preferredDate: '',
            preferredTime: '',
            movieWish: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            schoolName: '',
            studentCount: undefined,
            adultsCount: undefined,
            childrenAge: '',
            concessionSales: '',
            specialRequests: ''
        }
    });

    const concessionSales = watch('concessionSales');

    const onSubmit = async (data: SchoolScreeningFormData) => {
        setIsProcessing(true);

        try {
            const success = await sendEmailSchoolScreening(data, turnstileToken ?? undefined);

            if (success) {
                setIsSuccess(true);
                reset();
                showToast({
                    toastType: toast.success,
                    messageIcon: <IconSuccessful16 />,
                    messageTitle: t('school-screening.success.title'),
                    messageBody: t('school-screening.success.message'),
                    backgroundColor: domainConfiguration.style.primaryColor,
                    textColor: domainConfiguration.style.onPrimaryColor
                });
            } else {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('school-screening.error.title'),
                    messageBody: t('school-screening.error.message')
                });
            }

            track('School Screening Form Submitted', {
                cinema: domainConfiguration.cinema?.name,
                domain: domainConfiguration.domain,
                cinemaId: domainConfiguration.cinema?.id,
                data: JSON.stringify(data, null, 2),
                success: success
            });
        } catch {
            showToast({
                toastType: toast.error,
                messageTitle: t('school-screening.error.title'),
                messageBody: t('school-screening.error.network-message')
            });
        } finally {
            setIsProcessing(false);
        }
    };

    if (isSuccess) {
        return (
            <div
                className="backdrop-blur-sm rounded-12 p-12 shadow-lg text-center"
                style={{ backgroundColor: domainConfiguration.style.surfaceColor }}>
                <IconSuccessful16
                    className="size-[48px] mx-auto mb-4"
                    style={{ color: domainConfiguration.style.primaryColor }}
                />
                <h3
                    className="text-headline-24 mb-2"
                    style={{ color: domainConfiguration.style.onBackgroundColor }}>
                    {t('school-screening.success.title')}
                </h3>
                <p
                    className="text-body-16"
                    style={{ color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8) }}>
                    {t('school-screening.success.message')}
                </p>
            </div>
        );
    }

    return (
        <div
            id="school-screening-form"
            className="backdrop-blur-sm rounded-12 p-12 shadow-lg"
            style={{ backgroundColor: domainConfiguration.style.surfaceColor }}>
            <form
                className="space-y-24"
                onSubmit={handleSubmit(onSubmit)}>
                {/* Movie Information */}
                <div className="space-y-4">
                    <label
                        className="block text-body-14 font-semibold"
                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                        {t('school-screening.form.movie-info')}
                    </label>
                    <div className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <FormInput
                                name="preferredDate"
                                label={t('school-screening.form.preferred-date')}
                                type="date"
                                register={register}
                                registerOptions={{ required: true }}
                                required
                            />
                            <FormInput
                                name="preferredTime"
                                label={t('school-screening.form.preferred-time')}
                                type="time"
                                register={register}
                            />
                        </div>
                        <FormInput
                            name="movieWish"
                            label={t('school-screening.form.movie-wish')}
                            type="text"
                            register={register}
                        />
                    </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                    <label
                        className="block text-body-14 font-semibold"
                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                        {t('school-screening.form.contact-info')}
                    </label>
                    <div className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <FormInput
                                name="firstName"
                                label={t('school-screening.form.first-name')}
                                type="text"
                                register={register}
                                registerOptions={{ required: true }}
                                required
                            />
                            <FormInput
                                name="lastName"
                                label={t('school-screening.form.last-name')}
                                type="text"
                                register={register}
                                registerOptions={{ required: true }}
                                required
                            />
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                            <FormInput
                                name="email"
                                label={t('school-screening.form.email')}
                                type="email"
                                register={register}
                                registerOptions={{ required: true }}
                                required
                            />
                            <FormInput
                                name="phone"
                                label={t('school-screening.form.phone')}
                                type="tel"
                                register={register}
                            />
                        </div>
                        <FormInput
                            name="schoolName"
                            label={t('school-screening.form.school-name')}
                            type="text"
                            register={register}
                            registerOptions={{ required: true }}
                            required
                        />
                    </div>
                </div>

                {/* Guest Count */}
                <div className="space-y-4">
                    <label
                        className="block text-body-14 font-semibold"
                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                        {t('school-screening.form.guest-count')}
                    </label>
                    <div className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <FormInput
                                name="studentCount"
                                label={t('school-screening.form.student-count')}
                                type="number"
                                inputMode="numeric"
                                min={1}
                                register={register}
                                registerOptions={{ valueAsNumber: true, required: true }}
                                required
                            />
                            <FormInput
                                name="adultsCount"
                                label={t('school-screening.form.adults-count')}
                                type="number"
                                inputMode="numeric"
                                min={1}
                                register={register}
                                registerOptions={{ valueAsNumber: true, required: true }}
                                required
                            />
                        </div>
                        <FormInput
                            name="childrenAge"
                            label={t('school-screening.form.children-age')}
                            type="text"
                            register={register}
                        />
                    </div>
                </div>

                {/* Catering */}
                <div className="space-y-4">
                    <label
                        className="block text-body-14 font-semibold"
                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                        {t('school-screening.form.catering')}
                    </label>
                    <div className="space-y-6">
                        {/* The design draws two checkboxes here, but "ja"/"nein" is an exclusive choice —
                            checkboxes would let a visitor tick both. Radios enforce the one answer the
                            cinema can act on and keep the required rule meaningful. */}
                        <fieldset className="space-y-3">
                            <legend
                                className="block text-body-14 font-semibold"
                                style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                {t('school-screening.form.concession-sales')} *
                            </legend>
                            <div className="flex flex-col gap-12">
                                {CONCESSION_SALES_OPTIONS.map((option) => (
                                    <label
                                        key={option}
                                        className="flex items-center gap-3 text-body-14 cursor-pointer"
                                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                        <input
                                            type="radio"
                                            value={option}
                                            {...register('concessionSales', { required: true })}
                                            className="appearance-none size-[16px] rounded-full border-2 cursor-pointer transition-all duration-150 outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--focus-ring-color)]"
                                            style={
                                                {
                                                    borderColor:
                                                        concessionSales === option
                                                            ? domainConfiguration.style.primaryColor
                                                            : withOpacity(
                                                                  domainConfiguration.style.onSurfaceColor,
                                                                  0.3
                                                              ),
                                                    backgroundColor:
                                                        concessionSales === option
                                                            ? domainConfiguration.style.primaryColor
                                                            : 'transparent',
                                                    // Inner ring in the on-primary color turns the filled
                                                    // circle into the usual dot-in-a-ring radio mark.
                                                    boxShadow:
                                                        concessionSales === option
                                                            ? `inset 0 0 0 2px ${domainConfiguration.style.onPrimaryColor}`
                                                            : undefined
                                                } as React.CSSProperties
                                            }
                                        />
                                        {t(`school-screening.form.${option}`)}
                                    </label>
                                ))}
                            </div>
                        </fieldset>

                        <FormTextarea
                            name="specialRequests"
                            label={t('school-screening.form.special-requests')}
                            register={register}
                            placeholder={t('school-screening.form.special-requests-placeholder')}
                        />
                    </div>
                </div>

                {/* Separator */}
                <div
                    className="w-full h-1"
                    style={{
                        backgroundColor: withOpacity(domainConfiguration.style.onSurfaceColor, 0.3)
                    }}
                />

                {/* Required Fields Note */}
                <div className="pt-4">
                    <p
                        className="text-body-12 text-center"
                        style={{
                            color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.7)
                        }}>
                        {t('school-screening.form.required-fields')}
                    </p>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                    <TurnstileWidget onVerify={setTurnstileToken} />
                    <Button
                        type="submit"
                        disabled={isProcessing}
                        className="w-full py-4 text-body-16 font-semibold transition-all duration-200 rounded-8"
                        css={{
                            backgroundColor: domainConfiguration.style.primaryColor,
                            color: domainConfiguration.style.onPrimaryColor,
                            opacity: isProcessing ? 0.6 : 1
                        }}
                        leadingIcon={isProcessing ? <IconLoading16 className="animate-spin" /> : undefined}>
                        {isProcessing ? t('school-screening.form.submitting') : t('school-screening.form.submit')}
                    </Button>
                </div>
            </form>
        </div>
    );
}
